import { useCallback, useEffect, useMemo, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Avatar,
  Tooltip,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  TableCell as MuiTableCell
} from '@mui/material';
import { Pagination } from '@mui/material';
import { Settings } from '@mui/icons-material';
import usePermissions from '../../hooks/usePermissions';
import { getUsersAsync, selectUsers } from '../../redux/slices/usersSlice';
import Title from '../../components/Title';
import TableCell from '../../components/TableCell';
import { formatDate } from '../../resources/functions';
import {
  GetUsersParams,
  SortFields,
  SortOrder,
  User,
  UserType
} from '../../resources/types/usersTypes';
import { selectAuth } from '../../redux/slices/authSlice';
import UserSettingsModal from '../../modals/UserSettingsModal';

const PREFIX = 'UsersPage';

const classes = {
  header: `${PREFIX}-header`,
  filters: `${PREFIX}-filters`,
  userTypeSelect: `${PREFIX}-userTypeSelect`,
  tableContainer: `${PREFIX}-tableContainer`
};

const Root = styled('div')(({ theme }) => ({
  padding: '0',
  minHeight: 'calc(100vh - 180px)'
}));

const FilterBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: 12,
  marginTop: 20,
  marginBottom: 20,
  flexWrap: 'wrap',
  alignItems: 'center',
  padding: '16px',
  backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#2A2A2A',
  borderRadius: 12,
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)'}`
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
  },
  transition: 'background-color 0.2s ease',
  cursor: 'pointer'
}));

const TableHeaderRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
}));

const TableHeaderCell = styled(MuiTableCell)(({ theme }) => ({
  fontWeight: 700,
  backgroundColor: 'transparent',
  borderBottom: `2px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`,
  padding: '12px 16px',
  '&:first-of-type': {
    paddingLeft: '20px'
  },
  '&:last-of-type': {
    paddingRight: '20px'
  },
  '&.sortable': {
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)',
    }
  }
}));


const LIMIT = 10;

const getUserName = ({ firstName, lastName, middleName }: User) => {
  return [lastName, firstName, middleName].filter(a => a).join(' ');
};

const getAvatarColor = (name: string) => {
  const colors = ['#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722'];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

const getUserInitial = (user: User) => {
  if (user.firstName) {
    return user.firstName.charAt(0).toUpperCase();
  }
  return 'К';
};

const sortOptions: Record<SortFields, { asc: string; desc: string }> = {
  firstName: {
    asc: 'firstName_asc',
    desc: 'firstName_desc'
  },
  mileage: {
    asc: 'mileage_asc',
    desc: 'mileage_desc'
  },
  balance: {
    asc: 'balance_asc',
    desc: 'balance_desc'
  },
  devices: {
    asc: 'devices_asc',
    desc: 'devices_desc'
  },
  birthday: {
    asc: 'birthday_asc',
    desc: 'birthday_desc'
  }
};

const UsersPage = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const users = useSelector(selectUsers);
  const dispatch = useDispatch();
  const checkPermission = usePermissions();
  const auth = useSelector(selectAuth);

  const userTypes = useMemo(() => ([
    { name: t('users.typeAll'), value: '' },
    { name: t('users.typeNormal'), value: 'normal' },
    { name: t('users.typeVip'), value: 'vip' }
  ]), [t]);

  const isSuperAdmin = useMemo(() => {
    return auth.info?.type === 'superAdmin';
  }, [auth.info?.type]);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [type, setType] = useState<UserType>('');
  // const [paymentId, setPaymentId] = useState<string>('');
  const [page, setPage] = useState(1);

  const [settingsUser, setSettingsUser] = useState<User | null>(null);
  const [currentField, setCurrentField] = useState<SortFields | null>(null);
  const [currentOrder, setCurrentOrder] = useState<SortOrder | null>(null);
  const [showDeletedUsers, setShowDeletedUsers] = useState<boolean>(false);

  const getUsers = useCallback(() => {
    const params: GetUsersParams = {
      limit: LIMIT,
      page
    };

    if (name) {
      params.name = name;
    }
    if (phone) {
      params.phone = phone;
    }

    if (type) {
      params.type = type;
    }

    params.deleted = showDeletedUsers;

    if (
      currentField &&
      currentOrder &&
      sortOptions[currentField][currentOrder]
    ) {
      params.sortType = sortOptions[currentField][currentOrder];
    }

    dispatch(getUsersAsync(params));
  }, [currentField, currentOrder, dispatch, name, page, phone, type, showDeletedUsers]);

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  const onChangePage = useCallback((e: any, nextPage: number) => {
    setPage(nextPage);
  }, []);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setPage(1);
  }, []);

  const handlePhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(e.target.value);
    setPage(1);
  }, []);

  const handleTypeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setType(e.target.value as UserType);
    setPage(1);
  }, []);

  const paginationCount = useMemo(
    () => Math.ceil(users.pagination.count / LIMIT),
    [users.pagination.count]
  );

  const toggleSortOrder = (field: SortFields) => {
    const newOrder = currentOrder === 'asc' ? 'desc' : 'asc';
    setCurrentField(field);
    setCurrentOrder(newOrder);
    return sortOptions[field][newOrder];
  };

  return (
    <Root>
      <Paper sx={{ border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', borderRadius: 2, padding: '20px' }}>
        <Title>{t('users.title')}</Title>
        
        <FilterBar>
          <TextField
            label={t('users.filterName')}
            value={name}
            onChange={handleNameChange}
            size="small"
            sx={{ minWidth: 200 }}
          />
          <TextField
            label={t('users.filterPhone')}
            value={phone}
            onChange={handlePhoneChange}
            size="small"
            sx={{ minWidth: 150 }}
          />
          <TextField
            select
            label={t('users.filterType')}
            value={type}
            onChange={handleTypeChange}
            size="small"
            sx={{ minWidth: 140 }}
          >
            {userTypes.map(userType => (
              <MenuItem key={userType.value} value={userType.value}>
                {userType.name}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Checkbox
                checked={showDeletedUsers}
                color='primary'
                onChange={(event, checked) => {
                  setShowDeletedUsers(checked);
                  setPage(1);
                }}
              />
            }
            label={t('users.showDeleted')}
          />
        </FilterBar>
        {Boolean(users.items.length) && (
          <div>
            <TableContainer className={classes.tableContainer} sx={{ overflow: 'hidden', borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableHeaderRow>
                  <TableHeaderCell>{t('users.colType')}</TableHeaderCell>
                  <TableHeaderCell 
                    className="sortable"
                    onClick={() => toggleSortOrder('firstName')}
                  >
                    {t('users.colName')}
                  </TableHeaderCell>
                  <TableHeaderCell>{t('users.colEmail')}</TableHeaderCell>
                  <TableHeaderCell>{t('users.colPhone')}</TableHeaderCell>
                  <TableHeaderCell 
                    className="sortable"
                    onClick={() => toggleSortOrder('birthday')}
                  >
                    {t('users.colBirthday')}
                  </TableHeaderCell>
                  <TableHeaderCell 
                    className="sortable"
                    onClick={() => toggleSortOrder('balance')}
                  >
                    {t('users.colBalance')}
                  </TableHeaderCell>
                  <TableHeaderCell 
                    className="sortable"
                    onClick={() => toggleSortOrder('mileage')}
                  >
                    {t('users.colMileage')}
                  </TableHeaderCell>
                  <TableHeaderCell 
                    className="sortable"
                    onClick={() => toggleSortOrder('devices')}
                  >
                    {t('users.colActiveSessions')}
                  </TableHeaderCell>
                  {showDeletedUsers && <TableHeaderCell>{t('users.colAccountStatus')}</TableHeaderCell>}
                  {(checkPermission('history') || isSuperAdmin) && <TableHeaderCell>{t('users.colActions')}</TableHeaderCell>}
                </TableHeaderRow>
              </TableHead>
              <TableBody>
                {users.items.map(user => {
                  const userInitial = getUserInitial(user);
                  const avatarColor = getAvatarColor(user.firstName || 'К');
                  const isVIP = user.type === 'vip';
                  
                  return (
                    <StyledTableRow
                      key={user._id}
                      onClick={() => setSettingsUser(user)}
                    >
                      <TableCell>
                        <Tooltip title={isVIP ? t('users.typeVip') : t('users.typeNormal')} arrow>
                          <Box sx={{ position: 'relative', display: 'inline-block' }}>
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: avatarColor,
                                fontSize: '1rem',
                                fontWeight: 600,
                                cursor: 'pointer'
                              }}
                            >
                              {userInitial}
                            </Avatar>
                            {isVIP && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: -2,
                                  right: -2,
                                  width: 16,
                                  height: 16,
                                  borderRadius: '50%',
                                  bgcolor: '#4ECDC4',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '2px solid',
                                  borderColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#1A1A1A'
                                }}
                              >
                                <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#000' }}>V</Typography>
                              </Box>
                            )}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {getUserName(user)}
                      </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phone}</TableCell>
                    <TableCell>{formatDate(user.birthday)}</TableCell>
                    <TableCell>{user.balance}</TableCell>
                    <TableCell>{user.mileage}</TableCell>
                    <TableCell>{user.devicesCount ?? 0}</TableCell>
                    {showDeletedUsers ? (
                      <TableCell>
                        <span style={{ color: user?.isDeleted ? 'red' : 'green', fontWeight: 600 }}>
                          {user?.isDeleted ? t('users.statusDeleted') : t('users.statusActive')}
                        </span>
                      </TableCell>
                    ) : null}
                    {checkPermission('history') || isSuperAdmin ? (
                      <TableCell>
                        <Tooltip title={t('common.settings')}>
                          <IconButton
                            size='medium'
                            onClick={(e: any) => {
                              e.stopPropagation();
                              setSettingsUser(user);
                            }}
                            sx={{
                              bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                              color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                              borderRadius: '8px',
                              '&:hover': { 
                                bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' 
                              }
                            }}
                          >
                            <Settings fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    ) : null}
                  </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}

      {!users.items.length && (
        <Box sx={{ 
          textAlign: 'center', 
          py: 8,
          backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
          borderRadius: 3,
          mt: 2
        }}>
          <Avatar sx={{ 
            width: 64, 
            height: 64, 
            bgcolor: 'rgba(80, 115, 248, 0.12)', 
            margin: '0 auto 16px',
            fontSize: 32
          }}>
            👥
          </Avatar>
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            {t('users.emptyTitle')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            {t('users.emptySubtitle')}
          </Typography>
        </Box>
      )}

      {paginationCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination 
            count={paginationCount} 
            page={page}
            onChange={onChangePage}
            size="large"
            sx={{
              '& .MuiPaginationItem-root': {
                borderRadius: 2,
                fontWeight: 600,
                '&.Mui-selected': {
                  backgroundColor: theme.palette.mode === 'light' ? '#000000' : '#FFFFFF',
                  color: theme.palette.mode === 'light' ? '#FFFFFF' : '#000000',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'light' ? '#212121' : '#E0E0E0'
                  }
                }
              }
            }}
          />
        </Box>
      )}
      </Paper>
      <UserSettingsModal
        open={settingsUser !== null}
        onClose={() => setSettingsUser(null)}
        user={settingsUser}
        isSuperAdmin={isSuperAdmin}
      />
    </Root>
  );
};

export default UsersPage;
